import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Justify, SearchBox } from 'tea-component';
import { useHistory, useIntl, getLocale } from 'umi';
import SourceTable from '../Table';
import TextMore from '../TextMore';
import ChannelLabel from '../ChannelLabel';
import DataPreviewLink from '../DataPreviewLink';
import Filter from '../Filter/SourceFilter';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage, buriedPointSearch } from '@/pages/home/utils/buriedPoint';
import { splitOldRouterPathNameList } from '../../../Metrics/component/IntelligenceMetrics';
import { gameTypeCN } from '../OpinionsSources';
import {
  buildDataSourceFilterOptionsFromTabResponse,
  getRecordIconSrc,
  getUrlBySelfs,
  resolveLegacyPageId,
  type TDataSourceFilterOption,
} from '../../../utils';
import { filterSourcesRecordsBySearch } from '../../../searchUtils';
import { useSearchParamsState } from '@/pages/intelligence/pages/GlobalView/MarketSize/hooks/useQuery';
import {
  getSource,
  getSourceUpdatedGamesTotalGames,
} from '@/pages/intelligence/services/sourceMetrics';

import './index.scss';

export default function IntelligenceSources(props) {
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [dataCN, setDataCN] = useState([]);
  const [dataEN, setDataEN] = useState([]);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const debounce = useRef({ interval: 0, delay: 500 }).current;

  const [searchValue, setSearchValue] = useState('');
  const [searchShow, setSearchShow] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [showCN, setShowCN] = useState([]);
  const [showEN, setShowEN] = useState([]);
  const [dataSourceOptions, setDataSourceOptions] = useState<TDataSourceFilterOption[]>([]);
  const tmpCNRef = useRef();
  const tmpENRef = useRef();
  const pageLogMes = dbiPageLogMes.sourcesMetricsSourcesIntelligenceSources;
  const history = useHistory();
  const getSourceValue = (sourceValue: string) => {
    const queryMetric = decodeURIComponent(sourceValue);
    if (!queryMetric) {
      return [];
    }
    return queryMetric?.split(',') || [];
  };
  const sourceValue = getSourceValue(useSearchParamsState('sourceValue', '')?.[0]);
  const resolvedSourceFromUrl = useMemo(
    () => sourceValue.map(resolveLegacyPageId).filter(Boolean),
    [sourceValue],
  );

  const getData = (params?: any, callback?: () => void) => {
    setLoading(true);

    const param = {
      tab: 'intelligence',
      get_games_total: false,
      ...params,
    };

    getSource(param)
      .then(async (data) => {
        setDataCN([]);
        setDataEN([]);

        data?.forEach((item) => {
          const tmp =
            item.list.map((item, idx) => ({
              ...item,
              id: idx + 1,
            })) || [];

          if (item.language === 'cn') {
            tmpCNRef.current = tmp;
            setDataCN(tmp);
          } else {
            tmpENRef.current = tmp;
            setDataEN(tmp);
          }
        });
        // 获取完第一个接口数据后，再次请求第二个接口获取Monthly updated games，Total games这两列数据
        const res = await getSourceUpdatedGamesTotalGames();
        const newDataSource = (dataSource) =>
          dataSource.map((item) => {
            for (let i = 0;i < res.length;i++) {
              const isEqual1 =
                item?.data_source?.toLowerCase() === 'data.ai' &&
                res[i]?.source_name?.toLowerCase() === 'App Annie'.toLowerCase();
              const isEqual2 =
                item?.data_source?.toLowerCase() !== 'data.ai' &&
                res[i]?.source_name?.toLowerCase() === item?.data_source?.toLowerCase();
              if (isEqual1 || isEqual2) {
                return {
                  ...item,
                  ated_games: res[i]?.ated_games,
                  total_games: res[i]?.total_games,
                };
              }
            }
            return item;
          });
        const cnList = newDataSource(tmpCNRef.current);
        const enList = newDataSource(tmpENRef.current);
        setDataCN(cnList);
        setDataEN(enList);
      })
      .catch((err) => {
        console.log(err);
        setIsError(true);
      })
      .finally(() => {
        setLoading(false);
        callback?.();
      });
  };

  // 获取表格容器引用
  const tableRef = useRef(null);

  const data = useMemo(
    () => (locale == 'zh-CN' && dataCN.length > 0 ? dataCN : dataEN),
    [locale, dataCN, dataEN],
  );

  const splitOpenOtherMonthlyFocus = (url: string, type: string) => {
    const isShow = url?.split(',')?.length > 1;
    const typeAppPlatform = type.split('/');
    if (!isShow) return;
    const listKeyPath = url.split(',');
    return typeAppPlatform.map((task, taskIndex) => ({
      text: task,
      url: splitOldRouterPathNameList(listKeyPath[taskIndex]),
      id: taskIndex,
    }));
  };
  const columns = [
    {
      key: 'id',
      fixed: 'left',
      align: 'left',
      width: 65,
      header: '#',
    },
    {
      key: 'data_source',
      fixed: 'left',
      align: 'left',
      width: 175,
      header: formatMessage({ id: 'dbi-sm-filter-datasource' }),
      render: (record: any) => {
        let replaceText = record.data_source;
        if (record.data_source === 'DataBrain') {
          replaceText = 'No-Update';
        } else if (record.data_source === 'Alinea') {
          replaceText = 'Alinea Analytics';
        }

        return <ChannelLabel
          onClickLink={(e) => {
            const url = e.url ? e.url : getUrlBySelfs(record?.data_source);
            history.push(url);
          }}
          record={record}
          iconUrl={getRecordIconSrc(record)}
          channel_name={record.data_source.toLowerCase()}
          // replaceText={record.data_source}
          replaceText={replaceText}
          typeDetails={splitOpenOtherMonthlyFocus(record.url, record?.game_type)}
        />;
      },
    },
    {
      key: 'ated_games',
      align: 'right',
      width: 200,
      header: formatMessage({ id: 'dbi-sm-table-monthlyupdate' }),
      // render: (cvm) => cvm?.ated_games ? cvm?.ated_games : '-',
      render: (cvm) => (
        <span className="tea-text-overflow" style={{ maxHeight: '85px', whiteSpace: 'pre-wrap' }}>
          {cvm.ated_games ? cvm.ated_games : '-'}
        </span>
      ),
    },
    {
      key: 'total_games',
      align: 'right',
      width: 170,
      header: formatMessage({ id: 'dbi-sm-table-totalgames' }),
      render: (cvm) => (cvm?.total_games ? cvm?.total_games : '-'),
    },
    {
      key: 'game_type',
      align: 'left',
      width: 200,
      header: formatMessage({ id: 'dbi-sm-filter-gametype' }),
      render: (cvm) =>
        cvm?.game_type ? (locale == 'zh-CN' ? gameTypeCN(cvm?.game_type) : cvm?.game_type) : '-',
    },
    {
      key: 'feature',
      align: 'left',
      width: 330,
      header: formatMessage({ id: 'dbi-sm-table-feature' }),
      render: (cvm) =>
        cvm?.feature ? (
          <TextMore
            data={cvm?.feature.replace(/(\r\n|\n|\r)/gm, '<br/>')}
            title={formatMessage({ id: 'dbi-sm-table-feature' })}
          />
        ) : (
          '-'
        ),
    },
    {
      key: 'service_model',
      align: 'left',
      width: 300,
      header: formatMessage({ id: 'dbi-sm-table-servicemodel' }),
      render: (cvm) => (
        <span className="tea-text-overflow" style={{ maxHeight: '85px', whiteSpace: 'pre-wrap' }}>
          {cvm.service_model ? cvm.service_model : '-'}
        </span>
      ),
    },
    {
      key: 'service_type',
      align: 'left',
      width: 180,
      header: formatMessage({ id: 'dbi-sm-filter-servicetype' }),
      render: (cvm) => (cvm?.service_type ? cvm?.service_type : '-'),
    },
    {
      key: 'process',
      align: 'left',
      width: 180,
      header: formatMessage({ id: 'dbi-sm-table-progress' }),
      render: (cvm) => (cvm?.process ? cvm?.process : '-'),
    },
    {
      key: 'web_site',
      align: 'left',
      width: 320,
      header: formatMessage({ id: 'dbi-sm-table-sourceurl' }),
      render: (cvm) => (cvm?.web_site ? cvm?.web_site : '-'),
    },
    {
      key: 'service_summary',
      align: 'left',
      width: 400,
      header: formatMessage({ id: 'dbi-sm-table-summary' }),
      render: (cvm) =>
        cvm?.service_summary ? (
          <TextMore
            data={cvm?.service_summary.replace(/(\r\n|\n|\r)/gm, '<br/>')}
            title={formatMessage({ id: 'dbi-sm-table-summary' })}
          />
        ) : (
          '-'
        ),
    },
    {
      key: 'industry_coverage',
      align: 'left',
      width: 280,
      header: formatMessage({ id: 'dbi-sm-table-industry-coverage' }),
      render: (cvm) => (
        <span className="tea-text-overflow" style={{ maxHeight: '85px', whiteSpace: 'pre-wrap' }}>
          {cvm.industry_coverage ? cvm.industry_coverage : '-'}
        </span>
      ),
    },
    {
      key: 'raw_data',
      align: 'left',
      width: 365,
      header: formatMessage({ id: 'dbi-sm-table-rawdata' }),
      render: (cvm) =>
        cvm?.raw_data ? (
          <TextMore
            title={formatMessage({ id: 'dbi-sm-table-rawdata' })}
            data={cvm?.raw_data?.replace(/(\r\n|\n|\r)/gm, '<br/>')}
          />
        ) : (
          '-'
        ),
    },
    {
      key: 'profit_model',
      align: 'left',
      width: 330,
      header: formatMessage({ id: 'dbi-sm-table-profitmodel' }),
      render: (cvm) => (
        <div style={{ maxHeight: '57px', whiteSpace: 'pre-wrap' }}>
          {cvm.profit_model ? cvm.profit_model : '-'}
        </div>
      ),
    },
    {
      key: 'data_usage',
      align: 'left',
      width: 350,
      header: formatMessage({ id: 'dbi-sm-table-datausage' }),
      render: (cvm) =>
        cvm?.data_usage ? (
          <TextMore
            data={cvm?.data_usage.replace(/(\r\n|\n|\r)/gm, '<br/>')}
            title={formatMessage({ id: 'dbi-sm-table-datausage' })}
          />
        ) : (
          '-'
        ),
    },
    {
      key: 'note',
      align: 'left',
      width: 500,
      header: formatMessage({ id: 'dbi-sm-table-note' }),
      render: (cvm) =>
        cvm?.note ? (
          <TextMore
            data={cvm?.note.replace(/(\r\n|\n|\r)/gm, '<br/>')}
            title={formatMessage({ id: 'dbi-sm-table-note' })}
          />
        ) : (
          '-'
        ),
    },
    {
      key: 'url',
      align: 'left',
      width: 150,
      header: formatMessage({ id: 'dbi_actions' }),
      render: (cvm) =>
        cvm?.url ? (
          <DataPreviewLink
            url={
              cvm?.url?.split(',')?.length === 1
                ? splitOldRouterPathNameList(cvm?.url)
                : `${splitOldRouterPathNameList(
                  cvm?.url?.split(',')[0],
                )},${splitOldRouterPathNameList(cvm?.url?.split(',')[1])}`
            }
          />
        ) : getUrlBySelfs(cvm?.data_source) ? (
          <Button type="link" onClick={() => history.push(getUrlBySelfs(cvm?.data_source))}>
            {formatMessage({ id: 'dbi_data_preview' })}
          </Button>
        ) : (
          <>-</>
        ),
    },
  ];

  useEffect(() => {
    getSource({ tab: 'intelligence', get_games_total: false })
      .then(data => {
        const options = buildDataSourceFilterOptionsFromTabResponse(data);
        if (options.length > 0) {
          setDataSourceOptions(options);
        }
      })
      .catch(err => {
        console.debug('fetch data source filter options failed', err);
      });
  }, []);

  useEffect(() => {
    getData(
      resolvedSourceFromUrl.length ? { source: resolvedSourceFromUrl } : {},
      () => {
      // 如果需要滚动
      const scrollToColumn = (key: string) => {
        // debugger;
        if (tableRef.current) {
          // 获取列元素
          const columnEl = tableRef.current?.querySelectorAll(`.tea-table__body col`);
          var pos = columns.findIndex((item) => item.key === key);
          if(pos > -1) {
            var body = tableRef.current?.querySelector(`.tea-table__body`);
            var width = 0
            for(var i = 2; i < pos; i++) {
              var w = Number(columnEl?.[i]?.style?.width?.replace('px', ''));
              if (isNaN(w)) {
                w = 0;
              }
              width += w;
            }
            if(body) {
              body.scrollTo({
                left: width,
                behavior: 'smooth'
              });
            }
          }
        }
      };
      var target = location.href.match(/scroll=([^&]+)/);
      if(target) {
        scrollToColumn(target[1]);
      }
      },
    );
    buriedPointPage(pageLogMes);
    // alert(2)
  }, []);

  const handleConfirm = async (value: any) => {
    // console.log("CONFIRM", value)
    getData(value);
    setSearchValue('');
    setSearchShow(false);
    setPageIndex(1);
  };

  const handleReset = () => {
    // console.log("RESET")
    setSearchValue('');
    setSearchShow(false);
  };

  /**
   * @description: 设置debounce，避免搜索频繁更改Report列表
   * @param {string} word 搜索关键字
   * @return {*}
   */
  const debounceSearchChange = (word: string) => {
    clearTimeout(debounce.interval);
    // @ts-ignore
    debounce.interval = setTimeout(() => {
      handleSearch(word);
      if (pageLogMes?.pageId) {
        buriedPointSearch({ extInfo: pageLogMes?.pageId, typeId: pageLogMes?.typeId }, word);
      }
    }, debounce.delay);
  };

  const handleSearch = (word: string) => {
    if (word == '') {
      setSearchShow(false);
    } else {
      const showEN = filterSourcesRecordsBySearch(dataEN, word);
      const showCN = filterSourcesRecordsBySearch(dataCN, word);
      setShowCN(showCN);
      setShowEN(showEN);
      setSearchShow(true);
      setPageIndex(1);
    }
  };

  const show = useMemo(
    () => (locale == 'zh-CN' && showCN.length > 0 ? showCN : showEN),
    [locale, showCN, showEN],
  );

  return (
    <>
      <Filter
        filterConfirm={handleConfirm}
        filterReset={handleReset}
        sourceValue={sourceValue}
        dataSourceOptions={dataSourceOptions}
      />
      <Justify
        className="dbi-imurreports-justify"
        style={{ marginTop: '20px' }}
        right={
          <SearchBox
            className="edit-search-box"
            placeholder={formatMessage({ id: 'dbi_search_for_source' })}
            value={searchValue}
            onSearch={() => {
              // debounceSearchChange();
            }}
            onChange={(word) => {
              setSearchValue(word);
              if (word == '') {
                setSearchShow(false);
              } else if (word.replace(/^\s*/g, '')) {
                debounceSearchChange(word);
              }
            }}
            onClear={() => {
              setSearchShow(false);
            }}
          />
        }
      />
      <SourceTable
        ref={tableRef}
        records={searchShow ? show : data}
        columns={columns}
        loading={loading}
        error={isError}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
      />
    </>
  );
}
